"use client";

import * as React from "react";
import { Select as SelectPrimitive } from "@base-ui/react/select";

import { cn } from "../../lib/utils";
import {
  IconSelector,
  IconCheck,
  IconChevronUp,
  IconChevronDown,
} from "@tabler/icons-react";

const Select = SelectPrimitive.Root;
const selectStyles = {
  group: "",
  value: "truncate",
  triggerIcon: "size-4 shrink-0 text-muted-foreground",
  positioner: "z-floating",
  content:
    "z-floating max-h-96 min-w-(--anchor-width) overflow-hidden rounded-lg border border-border bg-popover p-1.5 text-sm leading-sm text-popover-foreground outline-none",
  label: "px-1 py-1 text-xs leading-xs text-muted-foreground",
  item: "relative flex cursor-default select-none items-center gap-1.5 rounded-md py-1 pl-1.5 pr-8 text-sm leading-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
  itemText: "flex items-center gap-1.5",
  itemIndicator: "absolute right-2 flex items-center justify-center",
  checkIcon: "size-4",
  separator: "mx-1.5 my-1 h-px bg-border",
  scrollUpButton: "flex cursor-default items-center justify-center py-1",
  scrollDownButton: "flex cursor-default items-center justify-center py-1",
};

const triggerSizes: Record<SelectSize, string> = {
  default: "h-8",
  sm: "h-7 gap-1 px-2 [font-size:var(--text-nav)] [line-height:var(--line-heights-nav)] [&_svg:not([class*='size-'])]:size-3.5",
  lg: "h-9 gap-2 px-3 text-sm leading-sm [&_svg:not([class*='size-'])]:size-4.5",
};

const triggerRadii: Record<SelectTriggerRadius, string | null> = {
  default: null,
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
};

const itemSizes: Record<SelectSize, string | null> = {
  default: null,
  sm: "gap-1 py-1 pl-1 pr-7 [font-size:var(--text-nav)] [line-height:var(--line-heights-nav)] [&_svg:not([class*='size-'])]:size-3.5",
  lg: "gap-2 py-2 pl-1.5 pr-8 text-sm leading-sm [&_svg:not([class*='size-'])]:size-4.5",
};

type SelectSize = "sm" | "default" | "lg";
type SelectTriggerRadius = "default" | "none" | "sm" | "md" | "lg" | "full";
const SelectSizeContext = React.createContext<SelectSize>("default");

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn(selectStyles.group, className)}
      {...props}
    />
  );
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn(selectStyles.value, className)}
      {...props}
    />
  );
}

function SelectTrigger({
  className,
  size = "default",
  radius = "default",
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  size?: SelectSize;
  radius?: SelectTriggerRadius;
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "flex w-full items-center justify-between gap-1 rounded-md border border-input bg-field px-2.5 text-sm leading-sm text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 [&_svg:not([class*='size-'])]:size-4",
        triggerSizes[size],
        triggerRadii[radius],
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={<IconSelector className={selectStyles.triggerIcon} />}
      />
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  size = "default",
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  alignItemWithTrigger = false,
  ...props
}: SelectPrimitive.Popup.Props & {
  size?: SelectSize;
} & Pick<
    SelectPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger"
  >) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className={selectStyles.positioner}
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-align-trigger={alignItemWithTrigger}
          className={cn(selectStyles.content, className)}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List>
            <SelectSizeContext.Provider value={size}>
              {children}
            </SelectSizeContext.Provider>
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn(selectStyles.label, className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  size,
  ...props
}: SelectPrimitive.Item.Props & { size?: SelectSize }) {
  const contextSize = React.useContext(SelectSizeContext)
  const itemSize = size ?? contextSize

  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(selectStyles.item, itemSizes[itemSize], className)}
      {...props}
    >
      <SelectPrimitive.ItemText className={selectStyles.itemText}>
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={<span className={selectStyles.itemIndicator} />}
      >
        <IconCheck className={selectStyles.checkIcon} />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn(selectStyles.separator, className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(selectStyles.scrollUpButton, className)}
      {...props}
    >
      <IconChevronUp />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(selectStyles.scrollDownButton, className)}
      {...props}
    >
      <IconChevronDown />
    </SelectPrimitive.ScrollDownArrow>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
